import { useMemo } from "react";

import { PieChart } from "@mantine/charts";
import { Box, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";


import { KpiDashboardResponse } from "types";

import classes from "./kpi.module.css";
import { getMetricLabelKey, getSubcategoryColor, getSubcategoryLabelKey } from "./kpiCatalog";
import { KPI_TEXT_GRAY } from "./kpiStatus";

interface KpiDistributionChartProps {
  dashboard: KpiDashboardResponse;
}

const KpiDistributionChart = ({ dashboard }: KpiDistributionChartProps) => {
  const { t } = useTranslation();

  const subcategories = useMemo(() => dashboard.subcategories ?? [], [dashboard.subcategories]);

  const data = useMemo(
    () =>
      subcategories
        .filter((sub) => sub.value > 0)
        .map((sub, index) => ({
          name: t(getSubcategoryLabelKey(sub.key)),
          value: sub.value,
          color: getSubcategoryColor(sub.key, index),
        })),
    [subcategories, t],
  );

  const hasData = data.length > 0;

  return (
    <Box className={classes.card}>
      {/* Title is the selected metric name (e.g. "Broj kvalifikacija"). */}
      <Text className={classes.cardTitle}>{t(getMetricLabelKey(dashboard.metric))}</Text>

      <Stack align="center" gap="lg" mt="md">
        {hasData ? (
          // Full pie (no hole) with the value rendered inside each slice.
          <PieChart
            data={data}
            size={200}
            withLabels
            labelsType="value"
            withLabelsLine={false}
            labelsPosition="inside"
            labelColor="#ffffff"
            withTooltip={false}
            strokeWidth={2}
            paddingAngle={2}
            // A small inner radius lets cornerRadius round the center corners too
            // (a full pie meets at a sharp point and can only round the outer corners).
            pieProps={{ cornerRadius: 6, innerRadius: "7%" }}
          />
        ) : (
          <Text size="sm" c={KPI_TEXT_GRAY}>
            {t("profilePage.kpi.emptyState")}
          </Text>
        )}

        {/* Legend below the chart — rounded-square swatches, no values (values are in the pie). */}
        <Stack gap={10} className={classes.legend}>
          {subcategories.map((sub, index) => (
            <Group key={sub.key} gap={10} wrap="nowrap">
              <span
                className={classes.legendSwatch}
                style={{ backgroundColor: getSubcategoryColor(sub.key, index) }}
              />
              <Text size="sm" c={KPI_TEXT_GRAY}>
                {t(getSubcategoryLabelKey(sub.key))}
              </Text>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default KpiDistributionChart;
